import { createSlice } from '@reduxjs/toolkit';
// services
import AdminAccountService from '../../services/adminAccount';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  adminList: [],
  admin: null,
  totalCount: 0,
  pagination: null
};

const slice = createSlice({
  name: 'adminAccount',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET ADMIN ACCOUNTS SUCCESS
    getAdminAccountsSuccess(state, action) {
      state.isLoading = false;
      const { data, pagination } = action.payload;
      state.adminList = data || [];
      state.pagination = pagination;
      state.totalCount = pagination?.total || 0;
    },

    // GET ADMIN ACCOUNT BY ID SUCCESS
    getAdminAccountByIdSuccess(state, action) {
      state.isLoading = false;
      state.admin = action.payload.data;
    },

    // CREATE ADMIN ACCOUNT SUCCESS
    createAdminAccountSuccess(state, action) {
      state.isLoading = false;
      const newAdmin = action.payload.data;
      if (newAdmin) {
        state.adminList.unshift(newAdmin);
        state.totalCount += 1;
      }
    },

    // UPDATE ADMIN ACCOUNT SUCCESS
    updateAdminAccountSuccess(state, action) {
      state.isLoading = false;
      const updatedAdmin = action.payload.data;
      if (updatedAdmin) {
        const index = state.adminList.findIndex((admin) => admin.id === updatedAdmin.id);
        if (index !== -1) {
          state.adminList[index] = updatedAdmin;
        }
        if (state.admin && state.admin.id === updatedAdmin.id) {
          state.admin = updatedAdmin;
        }
      }
    },

    // DELETE ADMIN ACCOUNT SUCCESS
    deleteAdminAccountSuccess(state, action) {
      state.isLoading = false;
      const deletedAdminId = action.payload;
      state.adminList = state.adminList.filter((admin) => admin.id !== deletedAdminId);
      state.totalCount -= 1;
    }
  }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

/**
 * Get all admin accounts
 */
export function getAdminAccounts(query = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    const result = await AdminAccountService.getList(query);

    if (result.success) {
      dispatch(
        slice.actions.getAdminAccountsSuccess({
          data: result.data || [],
          pagination: result.pagination || { total: 0, page: 1, limit: 10, totalPages: 0 }
        })
      );
    } else {
      dispatch(slice.actions.hasError(result.errors));
    }

    return result;
  };
}

// ----------------------------------------------------------------------

/**
 * Get admin account by ID
 */
export function getAdminAccountById(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    const result = await AdminAccountService.getById(id);

    if (result.success) {
      dispatch(
        slice.actions.getAdminAccountByIdSuccess({
          data: result.data
        })
      );
    } else {
      dispatch(slice.actions.hasError(result.errors));
    }

    return result;
  };
}

// ----------------------------------------------------------------------

/**
 * Create admin account
 */
export function createAdminAccount(data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    const result = await AdminAccountService.create(data);

    if (result.success) {
      dispatch(
        slice.actions.createAdminAccountSuccess({
          data: result.data
        })
      );
    } else {
      dispatch(slice.actions.hasError(result.errors));
    }

    return result;
  };
}

// ----------------------------------------------------------------------

/**
 * Update admin account
 */
export function updateAdminAccount(id, data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    const result = await AdminAccountService.update(id, data);

    if (result.success) {
      dispatch(
        slice.actions.updateAdminAccountSuccess({
          data: result.data
        })
      );
    } else {
      dispatch(slice.actions.hasError(result.errors));
    }

    return result;
  };
}

// ----------------------------------------------------------------------

/**
 * Delete admin account
 */
export function deleteAdminAccount(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    const result = await AdminAccountService.delete(id);

    if (result.success) {
      dispatch(slice.actions.deleteAdminAccountSuccess(id));
    } else {
      dispatch(slice.actions.hasError(result.errors));
    }

    return result;
  };
}

// ----------------------------------------------------------------------

/**
 * Assign merchant access to admin
 */
export function assignMerchantAccess(adminId, data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    const result = await AdminAccountService.assignMerchantAccess(adminId, data);

    if (result.success) {
      // Refresh admin data after assigning access
      await dispatch(getAdminAccountById(adminId));
    } else {
      dispatch(slice.actions.hasError(result.errors));
    }

    return result;
  };
}

// ----------------------------------------------------------------------

/**
 * Revoke merchant access from admin
 */
export function revokeMerchantAccess(adminId, merchantId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    const result = await AdminAccountService.revokeMerchantAccess(adminId, merchantId);

    if (result.success) {
      // Refresh admin data after revoking access
      await dispatch(getAdminAccountById(adminId));
    } else {
      dispatch(slice.actions.hasError(result.errors));
    }

    return result;
  };
}

// ----------------------------------------------------------------------

/**
 * Check if admin can impersonate merchant
 */
export function checkImpersonatePermission(adminId, merchantId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    const result = await AdminAccountService.checkImpersonatePermission(adminId, merchantId);

    if (!result.success) {
      dispatch(slice.actions.hasError(result.errors));
    } else {
      dispatch(slice.actions.hasError(false));
    }

    return result;
  };
}
